import React, { useContext, useMemo, useState } from "react";
import { BodyContainer } from "App/styles";
import { MainTitle } from "components/MainTitle";
import EntryWorkflow from "components/EntryWorkflow";
import { CompetitionContext } from "contexts/CompetitionContext";
import { WorkflowStep } from "model/dictionary";
import Confirmation from "components/Confirmation";
import { InputField, SelectField } from "basics";
import { CleanButton } from "basics/buttons";
import CheckIcon from "icons/CheckIcon";
import EmailIcon from "icons/EmailIcon";
import useTimeAgo from "hooks/useTimeAgo";
import useJudgesVoting, { AwardVotingStatus } from "hooks/useJudgesVoting";
import { normalizeText } from "helpers";
import {
  PageDescription,
  Filter,
  Summary,
  SummaryItem,
  List,
  Row,
  Judge,
  JudgeName,
  JudgeEmail,
  Reminder,
  Awards,
  AwardChip,
  Side,
  Progress,
  RemindButton,
  Done,
  ConfirmBox,
  Empty,
  Notice,
} from "./styles";

const idleLabel = {
  [AwardVotingStatus.NO_FINALISTS]: "sin finalistas",
  [AwardVotingStatus.CLOSED]: "con ganador",
};

const dayMonth = (time) =>
  new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long" }).format(
    new Date(time)
  );

const plural = (count, singular, pluralForm) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/**
 * Estado de la votación: qué jurados ya votaron en cada uno de sus premios
 * —no a quién, eso no sale del servidor— y un recordatorio por mail para los
 * que tienen votos pendientes, con la lista de los premios que les faltan.
 */
export default function VotingStatus() {
  const {
    loading,
    error,
    rows,
    competitions,
    competitionId,
    selectCompetition,
    isCurrent,
    sendReminder,
  } = useJudgesVoting();

  // Los recordatorios siguen la ventana de la Votación (ver `getStepWindows`
  // de `CompetitionContext`): fuera de ella el link del mail lleva a una
  // pantalla donde el jurado no puede votar. Las ediciones anteriores son
  // siempre de consulta.
  const { stepStatus = {}, stepWindows = {} } = useContext(CompetitionContext);
  const votingStatus = stepStatus[WorkflowStep.VOTING];
  const votingWindow = stepWindows[WorkflowStep.VOTING];

  const canRemind = isCurrent && votingStatus === "open";
  const votingClosed = !isCurrent || votingStatus === "after";

  const notice = !competitionId
    ? null
    : !isCurrent
      ? "Está viendo una edición anterior: el estado es sólo de consulta y no se pueden mandar recordatorios."
      : votingStatus === "before"
        ? `La votación abre el ${dayMonth(
            votingWindow.start
          )}: hasta entonces no se pueden mandar recordatorios.`
        : votingStatus === "after"
          ? `La votación cerró el ${dayMonth(
              votingWindow.end
            )}: ya no se pueden mandar recordatorios. Si hace falta reabrirla, se corrigen las fechas en Administración > Competiciones.`
          : null;

  const [search, setSearch] = useState("");
  const [searchKey, setSearchKey] = useState(0);

  // El uid del jurado al que se le está confirmando el recordatorio: un solo
  // panel de confirmación abierto a la vez.
  const [confirming, setConfirming] = useState();

  const pendingRows = useMemo(
    () => rows.filter((row) => row.pending.length > 0),
    [rows]
  );

  const visibleRows = useMemo(
    () =>
      search
        ? rows.filter((row) =>
            normalizeText(`${row.name} ${row.email}`).includes(search)
          )
        : rows,
    [rows, search]
  );

  const onReset = () => {
    setSearch("");
    setSearchKey((prev) => prev + 1);
    selectCompetition("");
  };

  const onSelectCompetition = (field, value) => {
    setConfirming();
    selectCompetition(value);
  };

  const onSendOne = (row) => {
    setConfirming();

    sendReminder(row);
  };

  const pendingVotes = pendingRows.reduce(
    (total, row) => total + row.pending.length,
    0
  );

  return (
    <BodyContainer>
      <MainTitle
        title="Estado de la votación"
        seoTitle="Estado de la votación"
      />

      <EntryWorkflow current={WorkflowStep.VOTING} />

      <PageDescription>
        Acá se ve qué jurados ya votaron en cada uno de sus premios. Sólo se
        muestra si votó o no, nunca a quién. Un premio cuenta como pendiente
        mientras tenga finalistas y todavía no tenga ganador. A los que les
        falta votar se les puede mandar un recordatorio por mail con la lista de
        premios pendientes.
      </PageDescription>

      <Filter>
        <SelectField
          key={`competition-${searchKey}`}
          id="competition"
          value={competitionId}
          options={competitions}
          onChange={onSelectCompetition}
          label="Edición"
        />
        <InputField
          key={searchKey}
          id="search"
          label="Buscar"
          placeholder="Nombre o mail del jurado"
          onChange={(field, value) => setSearch(normalizeText(value))}
        />
        <CleanButton onClick={onReset}>Limpiar</CleanButton>
      </Filter>

      {notice && <Notice>{notice}</Notice>}

      {!loading && !error && rows.length > 0 && (
        <Summary>
          <SummaryItem>
            <strong>{rows.length}</strong>
            {rows.length === 1 ? "jurado" : "jurados"}
          </SummaryItem>
          <SummaryItem>
            <strong>{rows.length - pendingRows.length}</strong>
            al día
          </SummaryItem>
          <SummaryItem $tone="pending">
            <strong>{pendingRows.length}</strong>
            con votos pendientes
          </SummaryItem>
          <SummaryItem $tone="pending">
            <strong>{pendingVotes}</strong>
            {pendingVotes === 1 ? "voto pendiente" : "votos pendientes"}
          </SummaryItem>
        </Summary>
      )}

      {loading && <Empty>Cargando el estado de la votación...</Empty>}

      {!loading && error && <Empty>{error}</Empty>}

      {!loading && !error && visibleRows.length === 0 && (
        <Empty>
          {rows.length === 0
            ? "No hay usuarios con rol de jurado."
            : "No hay jurados que coincidan con la búsqueda."}
        </Empty>
      )}

      {!loading && !error && visibleRows.length > 0 && (
        <List>
          {visibleRows.map((row) => (
            <JudgeRow
              key={row.uid}
              row={row}
              canRemind={canRemind}
              votingClosed={votingClosed}
              confirming={confirming === row.uid}
              onAsk={() => setConfirming(row.uid)}
              onCancel={() => setConfirming()}
              onSend={() => onSendOne(row)}
            />
          ))}
        </List>
      )}
    </BodyContainer>
  );
}

function JudgeRow({
  row,
  canRemind,
  votingClosed,
  confirming,
  onAsk,
  onCancel,
  onSend,
}) {
  const hasPending = row.pending.length > 0;

  return (
    <Row $pending={hasPending}>
      <Judge>
        <JudgeName>{row.name}</JudgeName>
        <JudgeEmail title={row.email}>{row.email}</JudgeEmail>
        {row.lastReminderAt && <LastReminder timestamp={row.lastReminderAt} />}
      </Judge>

      <Awards aria-label="Premios asignados">
        {row.awards.length === 0 && (
          <AwardChip $tone="idle">Sin premios asignados</AwardChip>
        )}
        {row.awards.map((award) => {
          const votable = award.status === AwardVotingStatus.OPEN;

          const tone = !votable ? "idle" : award.voted ? "voted" : "pending";

          return (
            <AwardChip
              key={award.id}
              $tone={tone}
              title={`${award.title} (${award.topic})`}
            >
              {tone === "voted" && <CheckIcon />}
              {award.title}
              <small>
                {votable
                  ? award.voted
                    ? "votó"
                    : votingClosed
                      ? "no votó"
                      : "pendiente"
                  : idleLabel[award.status]}
              </small>
            </AwardChip>
          );
        })}
      </Awards>

      <Side>
        <Progress $pending={hasPending}>
          <strong>
            {row.votedCount}/{row.votableCount}
          </strong>
          votados
        </Progress>

        {hasPending ? (
          canRemind && (
            <RemindButton
              type="button"
              title="Enviar un recordatorio por mail"
              onClick={onAsk}
            >
              <EmailIcon />
              Recordar
            </RemindButton>
          )
        ) : (
          <Done>
            <CheckIcon />
            {row.votableCount > 0 ? "Al día" : "Nada para votar"}
          </Done>
        )}
      </Side>

      {confirming && (
        <ConfirmBox>
          <Confirmation
            type="confirm"
            text={`¿Enviar un recordatorio a ${row.email} por ${plural(
              row.pending.length,
              "premio pendiente",
              "premios pendientes"
            )}?`}
            onClick={onSend}
            cancel={onCancel}
          />
        </ConfirmBox>
      )}
    </Row>
  );
}

function LastReminder({ timestamp }) {
  const timeago = useTimeAgo(timestamp);

  return <Reminder>Último recordatorio {timeago}</Reminder>;
}
